import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from '@suivipma/shared'
import { config } from '@/config'
import { ApiError } from '@/lib/errors'
import { getAccessToken, renewLocalSession } from '@/lib/session'
import { requestStepUp } from '@/modules/auth/stepUp'

/**
 * Client HTTP typé par le contrat OpenAPI (`@suivipma/shared`) — **seule** façon
 * d'appeler l'API depuis la SPA. Deux responsabilités portées par ses middlewares :
 *
 *   1. poser l'access token (renouvelé d'avance s'il expire) sur chaque requête ;
 *   2. sur un 401, renouveler **une fois** puis rejouer la requête ; si la session est
 *      morte, ramener à la mire avec le chemin courant en `redirect` ;
 *   3. sur un 403 `STEP_UP_REQUIRED`, ouvrir la modale de second facteur, puis rejouer la
 *      requête avec l'access token monté.
 */
export const api = createClient<paths>({
  baseUrl: config.apiOrigin,
  credentials: 'include',
})

/** Requêtes qui portent elles-mêmes leur preuve de session : pas de Bearer, pas de rejeu. */
const PUBLIC_SESSION_PATHS = new Set(['/api/auth/refresh', '/api/auth/logout'])

const RETRIED = new WeakSet<Request>()
const STEPPED_UP = new WeakSet<Request>()

/**
 * Copie **non consommée** de chaque requête : le corps d'une requête envoyée est épuisé,
 * un rejeu doit repartir d'un clone pris avant l'envoi.
 */
const REPLAYABLE = new WeakMap<Request, Request>()

async function replayWith(request: Request, accessToken: string): Promise<Response> {
  const source = REPLAYABLE.get(request) ?? request
  const retry = source.clone()
  REPLAYABLE.set(request, source)
  retry.headers.set('Authorization', `Bearer ${accessToken}`)
  return fetch(retry)
}

const auth: Middleware = {
  async onRequest({ request, schemaPath }) {
    if (PUBLIC_SESSION_PATHS.has(schemaPath)) return request
    const token = await getAccessToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    REPLAYABLE.set(request, request.clone())
    return request
  },
  async onResponse({ request, response, schemaPath }) {
    if (PUBLIC_SESSION_PATHS.has(schemaPath)) return response

    if (response.status === 401 && !RETRIED.has(request)) {
      RETRIED.add(request)
      const renewed = await renewLocalSession()
      if (renewed) return replayWith(request, renewed.accessToken)
      if (!window.location.pathname.startsWith('/auth/')) {
        const target = window.location.pathname + window.location.search
        window.location.assign(`/auth/login?expired=1&redirect=${encodeURIComponent(target)}`)
      }
      return response
    }

    if (
      response.status === 403 &&
      !STEPPED_UP.has(request) &&
      !window.location.pathname.startsWith('/auth/')
    ) {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as { code?: unknown } | null
      if (body?.code === 'STEP_UP_REQUIRED') {
        STEPPED_UP.add(request)
        const minLoa = Number(response.headers.get('x-step-up-required')) || 2
        if (await requestStepUp(minLoa)) {
          const token = await getAccessToken()
          if (token) return replayWith(request, token)
        }
      }
    }
    return response
  },
}

api.use(auth)

/**
 * Convertit la réponse `{ data, error, response }` d'openapi-fetch en valeur ou en
 * `ApiError` rejetée (statut conservé : TanStack Query ne rejoue pas les 4xx).
 */
export function unwrap<T>({ data, error, response }: { data?: T; error?: unknown; response: Response }): T {
  if (error !== undefined || !response.ok) {
    const message = (error as { message?: unknown } | undefined)?.message
    throw new ApiError(
      response.status,
      typeof message === 'string' ? message : `HTTP ${response.status}`,
      error,
    )
  }
  // Les 204 n'ont pas de corps : `data` est alors `undefined` et c'est attendu.
  return data as T
}
